/**
 * Shared Exception Data Table
 *
 * Renders exception data using ExceptionColumnDef configuration.
 * Supports two variants:
 * - inspector: light headers, table-only, fpCode validation
 * - platform: teal headers, sticky header (used inside BaseReportPage)
 */
import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseIcon from '@mui/icons-material/Close'
import type {
  ExceptionColumnDef,
  ExceptionConfig,
  ExceptionContext,
  ExceptionRowIdentity,
} from '../../../config/exceptions/types'
import { compareTargetFromRow } from '../../../config/exceptions/shared/compareSalesTarget'
import type { ActiveFilterChip } from '../../inspector/shared/ActiveFilterChips'
import {
  findSearchFilter,
  renderFilterControl as renderFilterControlShared,
  renderSearchInput as renderSearchInputShared,
} from '../../inspector/shared/exceptionFilterControls'
import { poolwerxColors } from '../../../theme/colors'
import {
  resultsCardSx,
  ACTIVE_ROW_BG,
  ROW_HOVER_BG,
  ROW_HOVER_SOFT,
  TABLE_ROW_DIVIDER,
  COLUMN_HEADER_BG_INSPECTOR,
  INNER_TABLE_CARD_BORDER,
  INNER_TABLE_CARD_RADIUS,
  INNER_TABLE_CARD_TOP_BORDER,
} from '../../../theme/exceptionTableStyles'
import {
  CHROME_OFFSET_CSS,
  Z_FROZEN_HEADER,
  Z_FROZEN_BODY,
  Z_TOOLBAR,
  computeStickyTops,
  getFreezeSx,
  readChromeOffsetPx,
  useFreezeMeta,
  useMeasuredHeight,
} from './stickyOffsets'
import ResultsToolbar from './ResultsToolbar'
import TableHeader from './TableHeader'
import TableBodyRows from './TableBodyRows'
import { useExceptionDrawer } from './useExceptionDrawer'
import { useExceptionTableSelection } from './useExceptionTableSelection'
import { useTableSorting } from './useTableSorting'
import type { ServerSortConfig } from './useTableSorting'
import { useTablePagination } from './useTablePagination'
import type { ServerPaginationConfig } from './useTablePagination'
import { useTableFiltering } from './useTableFiltering'
import type { ServerSearchConfig, SummaryChipsControl } from './useTableFiltering'
import { buildEffectiveColumns } from './columns/injectColumns'
import CompareSalesDialog from '../../inspector/shared/CompareSalesDialog'
import CompareSalesUrlSync from '../../inspector/shared/CompareSalesUrlSync'
import DuplicateGroupCompareDialog from '../../inspector/shared/duplicateCompare/DuplicateGroupCompareDialog'
import useCompareSalesDialog from '../../../hooks/useCompareSalesDialog'
import ExceptionDrawer from '../../inspector/shared/ExceptionDrawer'
import BulkActionBanner from '../../inspector/shared/BulkActionBanner'
import { getRowSummaryTitle } from '../../../utils/exceptionSummary'
import {
  compositeKey,
  useExceptionNoteCounts,
} from '../../../hooks/useExceptionNoteCounts'
import type { CountKeyParts } from '../../../hooks/useExceptionNoteCounts'
import { useAuth } from '../../../contexts/AuthContext'
import {
  canSeeExceptionNotes,
  canSeeExceptionDrawer,
  canBlockExceptions,
} from '../../../services/auth'

// The pagination / filter contracts live with their concerns in
// `useTablePagination` / `useTableFiltering`; re-exported here so existing
// importers of the table keep working.
export type { ServerPaginationConfig } from './useTablePagination'
export type { ServerSearchConfig, SummaryChipsControl } from './useTableFiltering'

interface DataViewTableProps {
  config: ExceptionConfig
  data: Record<string, any>[]
  /**
   * Full API response for the report. Used to read the `counts` block when
   * `config.summaryChips` is set — other fields are ignored. Optional.
   */
  response?: Record<string, any>
  variant: ExceptionContext
  fpCode?: string
  isLoading?: boolean
  /**
   * Opt into server-driven pagination. When omitted, the table paginates
   * the supplied `data` client-side as before.
   */
  serverPagination?: ServerPaginationConfig
  /**
   * Opt into server-driven search for the `'search'` filter in
   * `config.filters`. When set, the input value comes from
   * `serverSearch.value` and changes call `serverSearch.onChange` (the
   * parent owns debounce / URL sync / refetch). When omitted, the
   * search filter runs client-side via its `predicate`.
   */
  serverSearch?: ServerSearchConfig
  /**
   * Opt into server-driven sorting. Header clicks on `sortableKeys`
   * delegate to `serverSort.onSortChange` (the parent owns the sort
   * params + refetch); other columns render without a sort affordance
   * and the rows pass through in server order. When omitted, sorting is
   * client-side as before.
   */
  serverSort?: ServerSortConfig
  /**
   * External control for `config.summaryChips` state. When set, the table
   * skips rendering its toolbar chip-strip (the panel hosts the chips)
   * and uses the external `activeKey` for row filtering. The setter is
   * called on chip clicks if the panel routes them back through here.
   */
  summaryChipsControl?: SummaryChipsControl
  /**
   * Filter values + setters lifted to the route via
   * `useExceptionFilterValues`. The panel renders the controls; this
   * table reads the values to drive its predicate. When the route
   * doesn't supply them (eg. platform reports rendering the table
   * standalone), the table falls back to internal state seeded from
   * `config.filters`.
   */
  filterValues?: Record<string, any>
  updateFilterValue?: (key: string, value: any) => void
  filterEnabled?: boolean
  /**
   * Breadcrumb chips rendered on the left of the toolbar. Computed by
   * the route. When the inspector panel isn't in play (platform
   * reports), the table renders nothing on the left and the right-side
   * controls cluster sits flush right.
   */
  activeFilterChips?: ActiveFilterChip[]
  /**
   * Optional content rendered in the LEFT zone of the Results toolbar
   * (before the active-filter chips). Lets callers like the platform
   * grouped report surface a per-group title + count chip inline with
   * the toolbar instead of in its own row above the table.
   */
  leftSlot?: React.ReactNode
  /**
   * Open/Closed lifecycle pill rendered inline with the Results title.
   * Treats lifecycle as a table view switch (not a filter), so the pill
   * lives in the sticky toolbar instead of the filter card and stays
   * reachable while the body scrolls. Pass ``null`` / omit on routes
   * whose envelope can't serve a Closed slice (live-classifier views)
   * so the pill is absent.
   */
  statusToggle?: React.ReactNode
  /**
   * Compact "Oldest open Xd" KPI chip rendered inline in the Results
   * toolbar after the lifecycle pill. Caller is responsible for
   * hiding it when the current view has no open items.
   */
  oldestOpenChip?: React.ReactNode
  /**
   * Label shown in the DialogTitle when the table is opened in fullscreen
   * mode (inspector variant). Inline rendering ignores this prop. Platform
   * variant has its own fullscreen pattern in BaseReportPage and skips the
   * inline fullscreen toggle entirely.
   */
  title?: React.ReactNode
  /**
   * Optional heading that replaces the default "Results" label in the
   * inspector ResultsToolbar. Use to inline the section title with the
   * table chrome (audit tabs do this so the section name reads where
   * the toolbar shows). Falls back to "Results" when omitted. Kept
   * separate from `title` so consumers can have a page-level header
   * above the table AND still show "Results" in the toolbar.
   */
  toolbarTitle?: React.ReactNode
  /**
   * Skip the outer "Results card" chrome (Paper padding, border, rounded
   * corners). Use when the table is already embedded inside a parent
   * card (e.g. Product Catalogue Review's tabbed Paper) where the chunky
   * `resultsCardSx` would read as a redundant card-in-card. The toolbar
   * + inner table card still render normally.
   */
  embedded?: boolean
  /**
   * When true, rows render with reduced opacity + a muted grey background
   * to make it visually obvious that the table is showing closed
   * (resolved or ignored) exceptions rather than the default open view.
   * Wired by DynamicExceptionRoute from the URL ?status=closed param.
   */
  isClosedView?: boolean
  /**
   * Message rendered in place of the table body when `data` is empty
   * and the table isn't loading. The ResultsToolbar (and statusToggle)
   * still render so the user can navigate back from an empty Closed
   * slice without being stranded. Omit on standalone usages that prefer
   * to show an empty table shell instead.
   */
  emptyMessage?: React.ReactNode
  /**
   * Optional callback fired after a successful single-row or bulk
   * Block/Unblock mutation. Parents that drive the data via a
   * fetch hook should re-trigger that hook here so the table reflects
   * the new lifecycle state. Without it, mutations succeed at the
   * API but the table still shows stale rows until the user navigates
   * away and back. The optional ``nextStatus`` argument is passed
   * through from the row-level kebab; the bulk banner omits it.
   */
  onAfterMutation?: (nextStatus?: string) => void
  /**
   * Short, human-friendly name of the current exception type (from
   * ``exception_types.name_short``). Surfaced inside the per-row
   * ``ExceptionDrawer`` so reviewers see "Helper products" instead of
   * the raw ``ex-product-ls-missing-helper-products`` code.
   */
  exceptionTypeName?: string
  /**
   * Business classification (``exception_types.exception_type``). Forwarded to
   * the per-row ``ExceptionDrawer`` so its header shows the same Process Issue
   * / Data Discrepancy badge as the report pages.
   */
  exceptionType?: string | null
  /**
   * Per-type rule from ``exception_types.action_policy``: when false, the
   * "Ignore" outcome is hidden in the per-row drawer AND the bulk
   * banner. Inspector tables are single-code, so one value covers the whole
   * table + its bulk selection. Defaults permissive. Backend re-enforces.
   */
  allowIgnore?: boolean
  /**
   * When the host route owns the drawer's open state (e.g. for URL
   * deep-linking via `?exceptionObject=<object_id>`), pass the current
   * `object_id` value here so the table can render the visual "active
   * row" tint without owning the drawer itself.
   *
   * Pair with `onRowOpen` so row clicks notify the host. When both
   * are provided, the table suppresses its internal `ExceptionDrawer`
   * mount; the host is expected to mount the drawer itself with the
   * resolved row (matches `pages/admin/ExceptionActivity/index.tsx`).
   */
  controlledExceptionObject?: string | null
  onRowOpen?: (row: Record<string, any> | null) => void
  /**
   * Controlled-mode bridge for the note-dot live update. The table
   * owns the ``useExceptionNoteCounts`` state internally, but when
   * the host mounts the drawer itself the host needs a way to bump
   * that state when the drawer posts / deletes a note. The table
   * calls this once after mount with its ``invalidate`` fn (stable
   * identity); the host stashes it in a ref and wires it into the
   * drawer's ``onCountDelta``. Without this, posting a note in
   * controlled mode silently no-ops the count -> the dot never appears
   * on a row that previously had zero notes.
   */
  onNoteCountsReady?: (
    invalidate: (parts: CountKeyParts, delta?: number) => void,
  ) => void
}

/**
 * Filter columns by context visibility
 */
const getVisibleColumns = (
  columns: ExceptionColumnDef[],
  variant: ExceptionContext
): ExceptionColumnDef[] => {
  return columns.filter(col => {
    if (!col.contexts) return true
    return col.contexts.includes(variant)
  })
}

// Sticky / scroll model
// ---------------------
// Three modes - chosen per render via `useInnerScroll` (derived from
// `variant` + `config.containInCard`):
//
// 1. Default inspector (`containInCard` unset / false):
//    `<TableContainer>` has `overflow: visible` on both axes; sticky
//    `<th>` cells + frozen columns bind to the window. Toolbar pins
//    under app chrome via `chromeOffset = var(--app-chrome-height,
//    120px)`. Page handles vertical scrolling. This is the original
//    model - suited to narrow tables that fit their parent card.
//
// 2. Contained inspector (`config.containInCard === true`):
//    `<TableContainer>` becomes a real viewport
//    (`overflow: auto` + dynamic `maxHeight` via `resolvedTableHeight`).
//    Sticky cells + frozen columns rebind to TableContainer. The
//    toolbar stays sticky-to-window (it's a sibling above the
//    wrapper, not a descendant). The TableContainer's own native
//    horizontal scrollbar at the bottom of the card is the only
//    horizontal scroll affordance. Opt-in per config for wide tables
//    (e.g. product-by-outlet).
//
// 3. Platform variant: same inner-scroll model as #2, with a static
//    `maxHeight: calc(100vh - 240px)` tuned for the admin layout.
//
// Inspector fullscreen Dialog: forces inner-scroll regardless,
// `chromeOffset = 0` because there is no fixed chrome inside the
// Dialog.
//
// All chrome heights + z-index constants live in `./stickyOffsets`.

export default function DataViewTable({
  config,
  data,
  response,
  variant,
  fpCode,
  isLoading,
  serverPagination,
  serverSearch,
  serverSort,
  summaryChipsControl,
  filterValues: externalFilterValues,
  updateFilterValue: externalUpdateFilterValue,
  filterEnabled: externalFilterEnabled,
  activeFilterChips,
  leftSlot,
  statusToggle,
  oldestOpenChip,
  title,
  toolbarTitle,
  embedded,
  isClosedView,
  emptyMessage,
  onAfterMutation,
  exceptionTypeName,
  exceptionType,
  allowIgnore = true,
  controlledExceptionObject,
  onRowOpen,
  onNoteCountsReady,
}: DataViewTableProps) {
  // Derived here AND inside `useTablePagination` (one-line, cheap): the
  // filter-reset effect below needs it in its deps before the hook runs.
  const isServerPaginated = !!serverPagination
  const { user: authUser } = useAuth()
  const notesGated = canSeeExceptionNotes(authUser)
  const drawerGated = canSeeExceptionDrawer(authUser)
  const bulkSelectGated =
    canBlockExceptions(authUser) &&
    variant === 'inspector' &&
    !config.disableBulkSelect
  const {
    drawerRow,
    setDrawerRow,
    activeDrawerRowKey: internalActiveDrawerRowKey,
  } = useExceptionDrawer(config.rowKeyField)
  // Controlled-mode: when the host route drives the drawer state via
  // ?exceptionObject=<object_id>, row clicks delegate to `onRowOpen`
  // and the visual "active row" tint reads from
  // `controlledExceptionObject` matched against `row.object_id` (not
  // `rowKeyField`, which can be a different identity in some configs).
  const isDrawerControlled =
    controlledExceptionObject !== undefined && onRowOpen !== undefined
  const activeDrawerRowKey = isDrawerControlled
    ? (() => {
        if (!controlledExceptionObject) return null
        const match = data.find(
          (r) =>
            String((r as ExceptionRowIdentity).object_id) ===
            controlledExceptionObject,
        )
        if (!match) return null
        const key = match[config.rowKeyField]
        return key !== undefined && key !== null ? String(key) : null
      })()
    : internalActiveDrawerRowKey
  const handleRowOpen = isDrawerControlled
    ? (row: Record<string, any>) => onRowOpen!(row)
    : setDrawerRow
  // Same identity precedence the drawer heading uses (ExceptionDrawer:
  // getRowTitle override, else the field-walk fallback) so the bulk
  // confirm modal names rows consistently across every domain.
  const resolveRowLabel = useCallback(
    (row: Record<string, any>) =>
      config.getRowTitle?.(row) ?? getRowSummaryTitle(row),
    [config.getRowTitle],
  )
  const {
    selectedTagIds,
    isBulkEligible,
    bulkColumnEligible,
    eligibleTagIdsOnPage,
    allEligibleSelected,
    someEligibleSelected,
    toggleAllOnPage,
    toggleRow,
    selectedRowsForBanner,
    handleBulkAfterMutation,
    clearSelection,
  } = useExceptionTableSelection({
    data,
    bulkSelectGated,
    onAfterMutation,
    resolveRowLabel,
  })
  // Bulk result feedback is now an app-level toast fired from
  // ``BulkActionBanner`` (via ``useErrorToast``); it survives the banner
  // unmounting on selection-clear, so no lifted state is needed here.
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const [fullscreenOpen, setFullscreenOpen] = useState(false)

  // Filter state (generic config filters, the filter toggle, summary
  // chips, fpCode validation) lives in `useTableFiltering` - lifted-first,
  // with internal fallback for standalone callers.
  const {
    filteredData,
    filterValues,
    setFilterValue,
    filterEnabled,
    setInternalFilterEnabled,
    hasFilter,
    effectiveFilters,
    chipMultiSelect,
    activeChipKey,
    setActiveChipKey,
    activeChipKeys,
    setActiveChipKeys,
    isChipActive,
    chipCounts,
    hasSummaryChips,
  } = useTableFiltering({
    config,
    data,
    variant,
    fpCode,
    response,
    externalFilterValues,
    externalUpdateFilterValue,
    externalFilterEnabled,
    summaryChipsControl,
    serverSearch,
    isServerPaginated,
  })

  const hasExpandable =
    !!config.expandable &&
    (config.expandable.contexts ?? ['inspector', 'platform']).includes(variant)
  // Column presence only - per-row eligibility is API-driven (the backend
  // stamps ``row.compare_sales`` on eligible rows; duplicate groups carry
  // ``group_id``; see ``rowDetailEnabledFor`` below), so ineligible rows
  // keep the column for alignment but render no button.
  const hasRowDetail = !!config.rowDetail
  const rowDetail = config.rowDetail
  // Row click triggers expand by default — compare-sales rowDetail keeps an
  // explicit button so users can read links/chips without accidentally
  // opening the dialog; duplicate-group configs opt their rows in via
  // ``rowDetail.rowClick`` (handled separately in TableBodyRows).
  const rowClickEnabled = hasExpandable && !!config.expandable?.rowClick
  const rowDetailRowClickEnabled =
    rowDetail?.type === 'duplicate-group' && !!rowDetail.rowClick

  const compareSalesDialog = useCompareSalesDialog()
  // Row whose duplicate group is open in DuplicateGroupCompareDialog.
  const [duplicateGroupRow, setDuplicateGroupRow] = useState<Record<
    string,
    any
  > | null>(null)

  const openRowDetail = (row: Record<string, any>) => {
    if (!rowDetail) return
    if (rowDetail.type === 'duplicate-group') {
      if (rowDetail.getGroupId(row)) setDuplicateGroupRow(row)
      return
    }
    const detailTarget = compareTargetFromRow(row)
    const effectiveFpCode = fpCode || (row.fp_code as string | undefined)
    if (detailTarget && effectiveFpCode) {
      compareSalesDialog.open({ fpCode: effectiveFpCode, ...detailTarget })
    }
  }

  // Per-row rowDetail-cell gate. compare-sales: the backend stamps
  // ``compare_sales`` only on eligible rows (build_compare_sales_target),
  // replacing the old config-level ``excludeCodes`` list. duplicate-group:
  // the row must carry a detector group id.
  const rowDetailEnabledFor = (row: Record<string, any>) =>
    rowDetail?.type === 'duplicate-group'
      ? !!rowDetail.getGroupId(row)
      : !!compareTargetFromRow(row)

  useEffect(() => {
    // Bulk-selection reset on data change lives inside
    // ``useExceptionTableSelection``. Bulk result feedback is an
    // app-level toast (see BulkActionBanner), so nothing to clear here.
    setExpandedRows({})
  }, [data])

  // Build the (exception_code, data_domain, object_id) tuple list once
  // per data change so the bulk-counts hook can hash + batch it. Empty
  // when the dataset doesn't carry the three tuple fields (defensive
  // skip - the notes column also skips itself in that case).
  const noteCountRows = useMemo(() => {
    if (!notesGated) return []
    const rows: Array<{
      exception_code: string
      data_domain: string
      object_id: string
      fp_code?: string
    }> = []
    for (const r of data) {
      if (!r) continue
      const {
        exception_code: ec,
        data_domain: dd,
        object_id: oid,
        fp_code: rowFp,
      } = r as ExceptionRowIdentity
      // All three tuple parts must be non-empty strings - the backend's
      // ``CountsRequest._RowKey`` rejects empty values with a 422 via
      // ``min_length=1`` on each field. Loose ``typeof`` checks let
      // empty strings slip through and used to surface as silent 422s
      // in the console after refetches.
      const ecOk = typeof ec === 'string' && ec.length > 0
      const ddOk = typeof dd === 'string' && dd.length > 0
      const oidStr =
        oid !== undefined && oid !== null ? String(oid) : ''
      if (ecOk && ddOk && oidStr !== '') {
        // Platform variant carries fp_code per row (table-level fpCode
        // is empty since the table spans FPs). Inspector variant relies
        // on the table-level fpCode and omits per-row fp_code so the
        // composite cache key stays short.
        const entry: {
          exception_code: string
          data_domain: string
          object_id: string
          fp_code?: string
        } = {
          exception_code: ec,
          data_domain: dd,
          object_id: oidStr,
        }
        // Same non-empty rule applies to per-row fp_code - the backend
        // gates it on ``min_length=1`` when present.
        if (
          variant === 'platform'
          && typeof rowFp === 'string'
          && rowFp.length > 0
        ) {
          entry.fp_code = rowFp
        }
        rows.push(entry)
      }
    }
    return rows
  }, [data, notesGated, variant])

  const {
    counts: noteCounts,
    siblingCounts: noteSiblingCounts,
    invalidate: invalidateNoteCount,
  } = useExceptionNoteCounts(fpCode, noteCountRows)

  // Controlled-mode bridge: publish ``invalidateNoteCount`` so the
  // host (which mounts the drawer itself) can wire it to the drawer's
  // ``onCountDelta``. ``invalidateNoteCount`` has stable identity
  // (``useCallback([])`` in the hook), so this fires once after mount.
  // Uncontrolled mode ignores the callback - the internal drawer wires
  // ``onCountDelta`` directly to ``invalidateNoteCount`` further
  // down.
  useEffect(() => {
    onNoteCountsReady?.(invalidateNoteCount)
  }, [onNoteCountsReady, invalidateNoteCount])

  // Whether the per-row ``ExceptionDrawer`` is wireable for this table.
  // Inspector requires a table-level ``fpCode`` (rows omit the field).
  // Platform reports span FPs - eligibility moves to the per-row check
  // inside ``TableBodyRows`` (one malformed row doesn't kill the
  // drawer for the whole table). Gated by ``canSeeExceptionDrawer`` -
  // any authenticated user (incl. FP_USER) can open the drawer; the
  // notes + lifecycle sections inside stay separately gated.
  const drawerEligible =
    drawerGated && (variant === 'platform' || (variant === 'inspector' && !!fpCode))

  const effectiveColumns = useMemo(
    () =>
      buildEffectiveColumns({
        config,
        data,
        variant,
        response,
        bulkColumnEligible,
        isBulkEligible,
        eligibleTagIdsOnPage,
        selectedTagIds,
        allEligibleSelected,
        someEligibleSelected,
        toggleAllOnPage,
        toggleRow,
      }),
    [
      config,
      data,
      variant,
      response,
      bulkColumnEligible,
      isBulkEligible,
      eligibleTagIdsOnPage,
      selectedTagIds,
      allEligibleSelected,
      someEligibleSelected,
      toggleAllOnPage,
      toggleRow,
    ],
  )

  const visibleColumns = useMemo(
    () => getVisibleColumns(effectiveColumns, variant),
    [effectiveColumns, variant]
  )

  // Skip rendering the group header row when no group has any visible
  // column under the current variant. Lets a single config define
  // inspector-only groups without producing an empty header row on the
  // platform variant (whose columns sit ungrouped).
  const hasColumnGroups = useMemo(() => {
    if (!config.columnGroups || config.columnGroups.length === 0) return false
    const visibleColumnsBeforeFreeze = getVisibleColumns(effectiveColumns, variant)
    return config.columnGroups.some(g =>
      visibleColumnsBeforeFreeze.some(c => c.group === g.id),
    )
  }, [config.columnGroups, effectiveColumns, variant])

  // Sticky-left + sticky-right column metadata. Lives in
  // `stickyOffsets.ts` alongside the top-sticky math so the entire
  // "where does this cell pin" concern is colocated.
  const { freezeMeta, freezeMetaRight } = useFreezeMeta(visibleColumns)

  /**
   * For each visible column, determine whether it's the last column of its
   * group and the group has `separatorAfter: true` — drives the 2px right
   * border used as a visual group boundary.
   */
  const groupSeparatorKeys = useMemo(() => {
    if (!hasColumnGroups || !config.columnGroups) return new Set<string>()
    const set = new Set<string>()
    for (const group of config.columnGroups) {
      if (!group.separatorAfter) continue
      const groupCols = visibleColumns.filter(c => c.group === group.id)
      if (groupCols.length === 0) continue
      set.add(groupCols[groupCols.length - 1].key)
    }
    return set
  }, [visibleColumns, config.columnGroups, hasColumnGroups])

  const totalColSpan = visibleColumns.length + (hasExpandable ? 1 : 0) + (hasRowDetail ? 1 : 0)

  const rowSplitSystems = config.rowSplit?.systems ?? null
  const isRowSplit = !!rowSplitSystems && variant === 'inspector'

  // Sort state + the first-render compound sort + the sorted-rows memo
  // live in `useTableSorting` (one concern per module).
  const { sortedData, orderBy, order, handleSort } = useTableSorting({
    filteredData,
    visibleColumns,
    config,
    variant,
    serverSort,
  })

  // Server-sort mode: only these keys render a sort affordance.
  const serverSortableKeys = useMemo(
    () => (serverSort ? new Set(serverSort.sortableKeys) : undefined),
    [serverSort],
  )

  // Page state (client + server modes), the page-reset effects and the
  // displayed-rows slice live in `useTablePagination`.
  const {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    displayedRows,
    showPagination,
    serverTotal,
    isTruncated,
  } = useTablePagination({
    sortedData,
    data,
    config,
    serverPagination,
    externalFilterValues,
    response,
  })

  // Measured-at-runtime heights feed `computeStickyTops` so the sticky
  // stack stays flush regardless of font / theme tweaks. The group
  // header row only mounts when `hasColumnGroups` is true; the toolbar
  // ref is only attached on the inspector variant - both cases produce
  // 0 when the underlying node is absent (see `useMeasuredHeight`).
  const groupHeaderRef = useRef<HTMLTableRowElement | null>(null)
  const groupHeaderHeight = useMeasuredHeight(groupHeaderRef)
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  const toolbarHeight = useMeasuredHeight(toolbarRef)
  // Bulk-select banner pins at the app-chrome bottom while a selection
  // is active; the toolbar/header stack pushes below it so all three
  // sticky surfaces stay reachable when scrolling.
  const bannerRef = useRef<HTMLDivElement | null>(null)
  const bannerHeight = useMeasuredHeight(bannerRef)

  // Anchor for "scroll Results card back under the chrome on page change".
  // Attached to the outermost Paper so the toolbar (its first child) lines
  // up flush below the app header + nav after a smooth scroll.
  const paperRef = useRef<HTMLDivElement | null>(null)
  // Inline TableContainer ref - drives the dynamic maxHeight computation
  // below. Only the inline-inspector render attaches it; the fullscreen
  // Dialog render leaves it null because DialogContent already provides
  // its own scroll context.
  const inlineTableContainerRef = useRef<HTMLDivElement>(null)

  // Dynamic max-height for the contained-card TableContainer. We size
  // the table viewport for the "user has scrolled past the page header
  // + filter card" state, NOT for the initial position - the filter
  // card is variable height and scrolls away normally, so factoring it
  // in artificially shortens the table.
  //
  // Math: viewport - chrome (sticky) - toolbar (sticky) - bottom gap.
  // The card has its own native horizontal scrollbar at its bottom edge,
  // so the gap just leaves room for the dev banner (sticky-bottom in
  // dev, ~40 px) plus ~24 px visual breathing room. Initially the
  // wrapper extends below viewport bottom; user scrolls down to bring
  // the table fully into the "below sticky chrome" area. Re-measures on
  // window resize and toolbar/chrome height changes.
  const [resolvedTableHeight, setResolvedTableHeight] = useState<number>(520)
  useLayoutEffect(() => {
    if (!useContainedScroll) return
    const measure = () => {
      const chromePx = readChromeOffsetPx()
      const toolbarPx = toolbarRef.current?.getBoundingClientRect().height ?? 0
      // 40 px dev banner (sticky at viewport bottom in dev) + ~24 px
      // breathing room so the card's native scrollbar never sits flush
      // against the banner. In prod the banner is absent and the 40 px
      // component is wasted; we keep the constant for visual
      // consistency rather than threading `isDev` through this hook.
      const bottomGap = 64
      const minHeight = 320
      setResolvedTableHeight(
        Math.max(minHeight, window.innerHeight - chromePx - toolbarPx - bottomGap),
      )
    }
    measure()
    window.addEventListener('resize', measure)
    // Toolbar height drives the available area below it; observe so we
    // re-measure when filter chips wrap or expanded filter state
    // changes the toolbar height.
    const ro = new ResizeObserver(measure)
    if (toolbarRef.current) ro.observe(toolbarRef.current)
    // The chrome height changes via a CSS variable (collapsed/expanded);
    // listen to window scroll - the chrome flip is what triggers the
    // change, and the measurement re-reads the CSS variable each call.
    window.addEventListener('scroll', measure, { passive: true })
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure)
      ro.disconnect()
    }
  })
  const scrollResultsIntoView = () => {
    const node = paperRef.current
    if (!node) return
    // Fullscreen Dialog has its own scroll context and no fixed chrome
    // above the table - scrollIntoView resolves inside the Dialog.
    if (fullscreenOpen) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    // Read the current chrome height from the CSS variable owned by
    // InspectorLayout - reflects collapsed state when chrome has shrunk
    // so pagination always anchors the toolbar flush under the live
    // chrome rather than under "the chrome that was there at mount".
    const rect = node.getBoundingClientRect()
    const targetY = window.scrollY + rect.top - readChromeOffsetPx()
    window.scrollTo({ top: targetY, behavior: 'smooth' })
  }

  const handleToggleExpand = (rowKey: string) => {
    setExpandedRows(prev => ({ ...prev, [rowKey]: !prev[rowKey] }))
  }

  const isInspector = variant === 'inspector'
  // Inspector tables opt into the contained-card model via
  // `config.containInCard`. When true: TableContainer becomes a real
  // viewport (inner-scroll) with its own native horizontal scrollbar at
  // the bottom of the card, and the Table renders at min-content width.
  // Default (false): original page-scroll model with overflow:visible
  // and sticky-to-window headers.
  const useContainedScroll = isInspector && config.containInCard === true

  // Freeze helpers stay here because they close over the shared
  // `freezeMeta` map; the variant-specific bg colour for header cells
  // mirrors the column-header bg so the freeze drop-shadow lands on the
  // same surface.
  const frozenHeaderBg = isInspector
    ? COLUMN_HEADER_BG_INSPECTOR
    : poolwerxColors.teal

  const getFreezeHeaderSx = (col: ExceptionColumnDef): Record<string, any> =>
    getFreezeSx(col, freezeMeta, freezeMetaRight, {
      backgroundColor: frozenHeaderBg,
      zIndex: Z_FROZEN_HEADER,
    })

  // Sticky cells need a backing colour so scrolled-under content doesn't
  // bleed through. Reading from the row-scoped `--row-bg` CSS variable
  // (set on the TableRow's sx) lets the row-level :hover rule update one
  // place and have every cell - including sticky ones - pick up the new
  // tint. Hardcoding the colour on each sticky cell would override the
  // hover, leaving the frozen columns un-highlighted as the user drags
  // the cursor across the row.
  const getFreezeBodySx = (col: ExceptionColumnDef): Record<string, any> =>
    getFreezeSx(col, freezeMeta, freezeMetaRight, {
      backgroundColor: 'var(--row-bg)',
      zIndex: Z_FROZEN_BODY,
    })

  const handleChipClick = (key: string) => {
    // Resolved-value form (not functional update) so the external setter
    // path in `summaryChipsControl` works the same as internal state.
    if (chipMultiSelect) {
      const isActive = activeChipKeys.includes(key)
      const next = isActive
        ? activeChipKeys.filter((k) => k !== key)
        : [...activeChipKeys, key]
      setActiveChipKeys(next)
    } else {
      setActiveChipKey(activeChipKey === key ? null : key)
    }
    setPage(0)
  }

  // Cross-concern glue: a filter write always lands the user back on
  // page 1. The state write itself lives in `useTableFiltering`.
  const updateFilterValue = (key: string, value: any) => {
    setFilterValue(key, value)
    setPage(0)
  }

  /**
   * Render the toolbar + table. Used twice on the inspector variant —
   * once inline, once inside the fullscreen Dialog — so the entire
   * table layout (toolbar, sticky headers, frozen columns, pagination)
   * lives in one place.
   *
   * Filter controls (search / multiselect / select / toggle) live in
   * the unified `DataViewFilterPanel` above the table. The table's
   * toolbar is one row:
   *
   *   [Results  chip › chip ×]    [count │ rows-per-page │ pagination │ expand]
   *           ↑ left                       ↑ one cohesive right cluster
   *
   * `rightSlot` is rendered at the far right (after a subtle divider).
   * Inline call -> fullscreen-open button. Fullscreen Dialog -> null
   * (close button lives in DialogTitle).
   *
   * `chromeOffset` is the px height of fixed chrome above the toolbar.
   * Embedded inspector: `var(--app-chrome-height, 120px)`. Fullscreen
   * Dialog + platform variant: 0. The toolbar pins under this offset
   * (sticky-to-window); the headers + frozen columns inside
   * TableContainer pin to TableContainer (sticky-to-wrapper).
   */
  const renderTableSection = (
    rightSlot: React.ReactNode | null,
    chromeOffset: number | string,
    tableContainerRef?: React.Ref<HTMLDivElement>,
  ): React.ReactNode => {
    // Inner-scroll = TableContainer is a real scrolling viewport, so
    // sticky <th> cells + frozen columns bind to it. Used by:
    //   - Platform variant (always - admin layout owns its chrome)
    //   - Inspector variant when `containInCard` is true (per-config
    //     opt-in for wide tables that need contained card scrolling)
    // Default inspector tables keep sticky-to-window (toolbar + headers
    // pin under app chrome) and the page handles scrolling.
    const useInnerScroll = !isInspector || useContainedScroll
    // When the bulk-select banner is visible, push the rest of the
    // sticky stack down by its rendered height so toolbar + header rows
    // pin flush BELOW the banner instead of underneath it.
    const showBanner = bulkColumnEligible && selectedTagIds.size > 0
    const stickyChromeOffset =
      showBanner && bannerHeight > 0
        ? typeof chromeOffset === 'number'
          ? chromeOffset + bannerHeight
          : `calc(${chromeOffset} + ${bannerHeight}px)`
        : chromeOffset
    const tops = computeStickyTops({
      useInnerScroll,
      chromeOffset: stickyChromeOffset,
      toolbarH: toolbarHeight,
      groupHeaderH: groupHeaderHeight,
      hasColumnGroups,
    })
    const toolbarStickyTop = tops.toolbar
    const groupHeaderStickyTop = tops.groupHeader
    const columnHeaderStickyTop = tops.columnHeader
    // Contained-card model only: pin the inset table card flush under the
    // sticky toolbar so its inner-scroll header engages from the first
    // scroll - matching the window-scroll tables. Without this the card
    // sits in normal flow and the whole card (header included) drifts up
    // with the page before inner-scroll takes over ("two-phase scroll").
    // Pins to the window because every ancestor up to it is overflow:visible
    // (resultsCardSx + the clip-path card Box). Toolbar stays above it
    // (Z_TOOLBAR) since the card keeps z-index:auto.
    const containedCardStickyTop =
      typeof stickyChromeOffset === 'number'
        ? `${stickyChromeOffset + toolbarHeight}px`
        : `calc(${stickyChromeOffset} + ${toolbarHeight}px)`
    // Banner pins at the raw chrome offset (above the toolbar). For
    // inner-scroll variants (platform) the banner doesn't render
    // anyway because bulkColumnEligible is inspector-only.
    const bannerStickyTop = useInnerScroll
      ? undefined
      : typeof chromeOffset === 'number'
        ? `${chromeOffset}px`
        : chromeOffset
    const totalCount = isServerPaginated ? serverPagination!.total : sortedData.length
    const pageCount = Math.max(1, Math.ceil(totalCount / Math.max(1, rowsPerPage)))
    const pageSizeOptions =
      serverPagination?.rowsPerPageOptions ?? [25, 50, 100, 250]
    const chips = activeFilterChips ?? []

    // Standalone usages (inspector Tools tabs) don't sit beneath the
    // DataViewFilterPanel and keep the legacy filter row inline. The
    // route-driven inspector path lifts state and skips this — the
    // panel hosts the controls instead. Platform reports also skip
    // this; page-level filtering (Country / Market / Exception Type)
    // lives in BaseReportPage, and configs' product filters / summary
    // chips belong only to the FP-side surfaces.
    const standalone = externalFilterValues === undefined && variant !== 'platform'
    const searchFilter = findSearchFilter(config.filters)
    const nonSearchFilters = effectiveFilters.filter((f) => f.type !== 'search')
    const inlineHasInlineChips =
      standalone && hasSummaryChips && !summaryChipsControl && !!config.summaryChips && !!chipCounts
    const inlineHasFilterRow =
      standalone &&
      (!!searchFilter || nonSearchFilters.length > 0 || inlineHasInlineChips || hasFilter)

    return (
      <>
        {isTruncated && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
            Showing first {data.length.toLocaleString()} of {serverTotal!.toLocaleString()} records — refine filters to see the rest.
          </Alert>
        )}
        {showBanner && (
          <BulkActionBanner
            selectedRows={selectedRowsForBanner}
            allowIgnore={allowIgnore}
            onClear={clearSelection}
            onAfterMutation={handleBulkAfterMutation}
            stickyTop={bannerStickyTop}
            stickyZIndex={Z_TOOLBAR + 1}
            bannerRef={bannerRef}
          />
        )}
        {inlineHasFilterRow && (
          <Box
            sx={{
              px: isInspector ? 0 : 2,
              py: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            {searchFilter && (
              <Box sx={{ minWidth: 240, flex: '0 1 280px' }}>
                {renderSearchInputShared({
                  filter: searchFilter,
                  value: serverSearch
                    ? serverSearch.value
                    : ((filterValues[searchFilter.key] ?? '') as string),
                  onChange: (next) => {
                    if (serverSearch) serverSearch.onChange(next)
                    else updateFilterValue(searchFilter.key, next)
                  },
                })}
              </Box>
            )}
            {nonSearchFilters.map((f) =>
              renderFilterControlShared({
                filter: f,
                value:
                  f.type === 'select'
                    ? (filterValues[f.key] ?? f.defaultValue ?? 'all')
                    : f.type === 'toggle'
                      ? !!(filterValues[f.key] ?? f.defaultEnabled)
                      : f.type === 'daterange'
                        ? (filterValues[f.key] ?? f.defaultValue ?? ['', ''])
                        : filterValues[f.key] ?? (f.type === 'multiselect' ? [] : ''),
                onChange: (next) => updateFilterValue(f.key, next),
              }),
            )}
            {inlineHasInlineChips && config.summaryChips && chipCounts && (
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontWeight: 500, mr: 0.5 }}
                >
                  Filter:
                </Typography>
                {config.summaryChips.categories.map((category) => {
                  const count = chipCounts[category.countField] ?? 0
                  const isActive = isChipActive(category.key)
                  return (
                    <Chip
                      key={category.key}
                      label={`${category.label}: ${count}`}
                      color={category.color}
                      variant={isActive ? 'filled' : 'outlined'}
                      onClick={() => handleChipClick(category.key)}
                      size="small"
                    />
                  )
                })}
              </Stack>
            )}
            {hasFilter && config.filterToggle && (
              <FormControlLabel
                sx={{
                  backgroundColor: poolwerxColors.tealLight,
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.25,
                  mr: 0,
                  ml: 0,
                }}
                control={
                  <Switch
                    size="small"
                    checked={filterEnabled}
                    onChange={(e) => setInternalFilterEnabled(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {config.filterToggle.label}
                  </Typography>
                }
              />
            )}
          </Box>
        )}
        <ResultsToolbar
          ref={toolbarRef}
          isInspector={isInspector}
          toolbarStickyTop={toolbarStickyTop}
          chips={chips}
          title={toolbarTitle}
          leftSlot={leftSlot}
          statusToggle={statusToggle}
          oldestOpenChip={oldestOpenChip}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          pageCount={pageCount}
          pageSizeOptions={pageSizeOptions}
          showPagination={showPagination}
          rightSlot={rightSlot}
          isLoading={!!isLoading}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n)
            setPage(0)
            scrollResultsIntoView()
          }}
          onPageChange={(p) => {
            setPage(p)
            scrollResultsIntoView()
          }}
        />
        {data.length === 0 && !isLoading && emptyMessage != null ? (
          // Sit outside the inner table card so the empty state reads
          // as a calm note under the toolbar rather than a third
          // outlined surface. The ResultsToolbar (with the Open/Closed
          // pill) above stays mounted so the user can step back from an
          // empty Closed slice. Caller owns the inner UI (icon /
          // heading / subtext / reset action) so the table stays
          // exception-agnostic.
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {emptyMessage}
          </Box>
        ) : (
        <Box
          sx={{
            position: 'relative',
            // Inset table card. Sub-card chrome - border + rounded
            // corners + clip-path - so the table reads as its own
            // rounded surface inside the all-white Results Paper. The
            // Paper's 16 px padding handles horizontal + bottom inset;
            // `mt: 1.5` here gives the 12 px gap between toolbar and
            // table. `clipPath` rounds the corners safely now that the
            // inner TableContainer is the scroll container - it
            // contains all horizontal overflow internally, so the clip
            // only ever shapes the visible card edges.
            // Inspector variant only - platform reports keep their
            // existing full-width chrome.
            ...(isInspector
              ? {
                  mt: 1.5,
                  border: INNER_TABLE_CARD_BORDER,
                  borderTop: INNER_TABLE_CARD_TOP_BORDER,
                  borderRadius: INNER_TABLE_CARD_RADIUS,
                  clipPath: `inset(0 round ${INNER_TABLE_CARD_RADIUS})`,
                  backgroundColor: '#FFFFFF',
                  // Pin the card under the toolbar in the contained-scroll
                  // model so its sticky header stays put on page scroll.
                  ...(useContainedScroll
                    ? {
                        position: 'sticky' as const,
                        top: containedCardStickyTop,
                      }
                    : {}),
                }
              : {}),
          }}
        >
        <TableContainer
          ref={tableContainerRef}
          sx={{
            // Three modes:
            //   - Contained inspector (`config.containInCard === true`):
            //     real viewport with dynamic max-height; sticky <th>
            //     cells + frozen columns rebind to TableContainer.
            //   - Platform variant: same model with the admin-layout
            //     static max-height (already shipping).
            //   - Default inspector: overflow:visible so the table
            //     stays sticky-to-window via the existing sticky
            //     chain; the page handles vertical scrolling.
            //
            // The toolbar above this wrapper stays sticky-to-window in
            // all three modes because it's a sibling, not a descendant.
            ...(useContainedScroll
              ? {
                  maxHeight: resolvedTableHeight,
                  overflow: 'auto' as const,
                }
              : !isInspector
                ? {
                    maxHeight: 'calc(100vh - 240px)',
                    overflow: 'auto' as const,
                  }
                : {
                    overflowX: 'visible' as const,
                    overflowY: 'visible' as const,
                  }),
            // Dim existing rows during refetch so the spinner overlay
            // reads as "this view is stale, new data on the way".
            opacity: isLoading && displayedRows.length > 0 ? 0.55 : 1,
            transition: 'opacity 120ms ease-out',
            pointerEvents: isLoading ? 'none' : 'auto',
          }}
        >
        <Table
          stickyHeader
          size="small"
          // Contained-card mode only: force the table to render at its
          // natural content width so it actually overflows the
          // TableContainer and produces a horizontal scrollbar inside
          // the card. Without `min-width: max-content`, MUI's
          // `display: table; width: 100%` + `table-layout: auto`
          // shrinks columns to fit and there's nothing to scroll.
          // Default inspector tables (and platform) keep the natural
          // `width: 100%` so they fit their parent.
          sx={useContainedScroll ? { minWidth: 'max-content' } : undefined}
        >
          <TableHeader
            ref={groupHeaderRef}
            visibleColumns={visibleColumns}
            columnGroups={config.columnGroups}
            hasColumnGroups={hasColumnGroups}
            hasExpandable={hasExpandable}
            hasRowDetail={hasRowDetail}
            rowDetailHeaderLabel={config.rowDetail?.headerLabel ?? ''}
            isInspector={isInspector}
            groupHeaderStickyTop={groupHeaderStickyTop}
            columnHeaderStickyTop={columnHeaderStickyTop}
            freezeMeta={freezeMeta}
            getFreezeHeaderSx={getFreezeHeaderSx}
            groupSeparatorKeys={groupSeparatorKeys}
            orderBy={orderBy}
            order={order}
            onSort={handleSort}
            sortableKeys={serverSortableKeys}
          />
          <TableBodyRows
            displayedRows={displayedRows}
            visibleColumns={visibleColumns}
            config={config}
            variant={variant}
            fpCode={fpCode}
            isInspector={isInspector}
            expandedRows={expandedRows}
            onToggleExpand={handleToggleExpand}
            rowClickEnabled={rowClickEnabled}
            openRowDetail={openRowDetail}
            hasExpandable={hasExpandable}
            hasRowDetail={hasRowDetail}
            rowDetailEnabledFor={rowDetailEnabledFor}
            rowDetailType={rowDetail?.type}
            rowDetailRowClickEnabled={rowDetailRowClickEnabled}
            isRowSplit={isRowSplit}
            rowSplitSystems={rowSplitSystems}
            totalColSpan={totalColSpan}
            groupSeparatorKeys={groupSeparatorKeys}
            getFreezeBodySx={getFreezeBodySx}
            tableRowDivider={TABLE_ROW_DIVIDER}
            rowHoverBg={isInspector ? ROW_HOVER_SOFT : ROW_HOVER_BG}
            isClosedView={isClosedView}
            activeRowKey={activeDrawerRowKey}
            // Stronger teal (MUI teal[100]) than ROW_HOVER_BG so the
            // active row is unmistakable even before the spotlight
            // dim kicks in on the other rows. Pairs with the teal
            // left-border accent applied inside TableBodyRows. Active
            // state only fires on the inspector variant (drawer is
            // gated to it), so the platform-hover/active colour
            // collision can't surface in practice.
            activeRowBg={ACTIVE_ROW_BG}
            openDrawer={drawerEligible ? handleRowOpen : undefined}
            drawerEligible={drawerEligible}
            getNoteCount={(row) => {
              // ``notesGated`` (not ``drawerEligible``) - FP users can
              // open the drawer but never see note counts. The count
              // fetch above already short-circuits on ``notesGated``,
              // so this guard is belt-and-braces against a stale map.
              if (!notesGated) return 0
              // Platform tables span FPs; the fetch payload composites
              // include ``fp_code`` so the lookup must match. Inspector
              // omits fp_code (same row's fp_code is always the table's
              // fpCode prop) and the hook keeps the bare three-part key.
              const k = compositeKey({
                exception_code: row.exception_code,
                data_domain: row.data_domain,
                object_id: String(row.object_id),
                ...(variant === 'platform' && row.fp_code
                  ? { fp_code: String(row.fp_code) }
                  : {}),
              })
              return noteCounts[k] ?? 0
            }}
            getSiblingNoteCount={(row) => {
              if (!notesGated) return 0
              const k = compositeKey({
                exception_code: row.exception_code,
                data_domain: row.data_domain,
                object_id: String(row.object_id),
                ...(variant === 'platform' && row.fp_code
                  ? { fp_code: String(row.fp_code) }
                  : {}),
              })
              return noteSiblingCounts[k] ?? 0
            }}
          />
        </Table>
      </TableContainer>
      {/* Centered overlay - large initial-load spinner when the table
        * is empty, smaller spinner pinned over the dimmed rows on
        * subsequent refetches so the user sees the in-flight state. */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: displayedRows.length === 0 ? 'flex-start' : 'center',
            justifyContent: 'center',
            pt: displayedRows.length === 0 ? 6 : 0,
            pointerEvents: 'none',
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <CircularProgress size={displayedRows.length === 0 ? 36 : 28} />
            <Typography variant="caption" color="text.secondary">
              Loading…
            </Typography>
          </Stack>
        </Box>
      )}
      </Box>
      )}
      </>
    )
  }

  const fullscreenButton = isInspector && (
    <Tooltip title="Fullscreen" arrow>
      <IconButton
        size="small"
        onClick={() => setFullscreenOpen(true)}
        aria-label="Open table in fullscreen"
        sx={{
          width: 32,
          height: 32,
          border: `1px solid ${poolwerxColors.tealAccent}`,
          borderRadius: 1,
          color: poolwerxColors.tealAccent,
          '&:hover': {
            backgroundColor: ROW_HOVER_SOFT,
          },
        }}
      >
        <OpenInFullIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  )

  // Inspector: outlined "Results card" with 16 px padding around the
  // toolbar + inner table card. Embedded: caller already provides outer
  // chrome (e.g. Product Catalogue Review's tabs Paper), so we strip the
  // padding/border/radius and let the toolbar sit flush against the
  // parent. Platform: lighter elevation, transparent background.
  const embeddedPaperSx = {
    overflow: 'visible',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    p: 0,
  } as const
  const paperSx = isInspector
    ? embedded
      ? embeddedPaperSx
      : resultsCardSx
    : { overflow: 'hidden', backgroundColor: 'transparent' }

  return (
    <>
      <Paper
        ref={paperRef}
        variant={isInspector && !embedded ? 'outlined' : 'elevation'}
        elevation={isInspector ? 0 : 1}
        sx={paperSx}
      >
        {renderTableSection(
          fullscreenButton || null,
          isInspector ? CHROME_OFFSET_CSS : 0,
          inlineTableContainerRef,
        )}
      </Paper>
      {isInspector && (
        <Dialog
          open={fullscreenOpen}
          onClose={() => setFullscreenOpen(false)}
          fullScreen
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: `1px solid ${TABLE_ROW_DIVIDER}`,
              py: 1.5,
            }}
          >
            <Typography variant="h6" component="div">
              {title ?? 'Table'}
            </Typography>
            <IconButton
              onClick={() => setFullscreenOpen(false)}
              aria-label="Exit fullscreen"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              // DialogContent is the scroll context. A small page-bg
              // tint + padding around the Results Paper makes the
              // wrapped card read as a distinct surface (matching the
              // inline view) rather than a borderless flood across the
              // Dialog.
              backgroundColor: '#f8f9fa',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Wrap the table section in the same Results Paper the
              * inline view uses so the expand experience matches the
              * polished card chrome (12 px radius, neutral border,
              * 16 px internal padding). chromeOffset = 0 because the
              * DialogTitle scrolls out of view naturally and leaves no
              * fixed chrome above the table. */}
            <Paper variant="outlined" sx={resultsCardSx}>
              {renderTableSection(null, 0)}
            </Paper>
          </DialogContent>
        </Dialog>
      )}
      {hasRowDetail && rowDetail?.type === 'compare-sales' && (
        <>
          <CompareSalesDialog {...compareSalesDialog.dialogProps} />
          {/* This table is the compare-sales deep-link owner: it reads
            * ?compareLsSale on load and reflects opens back into the URL. */}
          <CompareSalesUrlSync
            fpCode={fpCode}
            target={compareSalesDialog.dialogProps.target}
            onOpen={compareSalesDialog.open}
          />
        </>
      )}
      {hasRowDetail && rowDetail?.type === 'duplicate-group' && (
        <DuplicateGroupCompareDialog
          open={duplicateGroupRow !== null}
          onClose={() => setDuplicateGroupRow(null)}
          // Inspector: table-level fpCode. Platform: rows span FPs, so fall
          // back to the active row's fp_code (same pattern as the drawer).
          fpCode={
            fpCode || (duplicateGroupRow?.fp_code as string | undefined) || ''
          }
          groupId={
            duplicateGroupRow ? rowDetail.getGroupId(duplicateGroupRow) : null
          }
          kind={rowDetail.groupKind}
          title={
            duplicateGroupRow
              ? rowDetail.getDialogTitle?.(duplicateGroupRow)
              : undefined
          }
          onAfterMutation={onAfterMutation}
        />
      )}
      {drawerEligible && !isDrawerControlled && (() => {
        // Inspector: table-level fpCode is the franchise for every row.
        // Platform: rows span FPs so the drawer's fpCode comes from the
        // active row. Both paths feed the same component with the same
        // tuple identity for notes + lifecycle calls. Suppressed in
        // controlled mode - the host route (e.g. DynamicExceptionRoute
        // when `?exceptionObject=` is wired) is expected to mount the
        // drawer itself so it can resolve the row via the central
        // drawer-detail endpoint (`useExceptionDrawerDetail`) when the
        // row isn't in the current page.
        const drawerFpCode = fpCode || (drawerRow?.fp_code as string | undefined)
        if (!drawerFpCode) return null
        return (
          <ExceptionDrawer
            open={drawerRow !== null}
            onClose={() => setDrawerRow(null)}
            row={drawerRow}
            fpCode={drawerFpCode}
            // The table's own drawer is mounted by exception-context surfaces
            // (single-code tool tables, platform reports) - the row's stamped
            // code is their current-exception bias, so float + expand it. The
            // no-bias browse surfaces (Data Explorer, admin DQ Workbench) mount
            // their OWN drawer in controlled mode and pass no focus.
            focusCode={drawerRow?.exception_code as string | undefined}
            exceptionTypeName={exceptionTypeName}
            exceptionType={exceptionType}
            allowIgnore={allowIgnore}
            getRowTitle={config.getRowTitle}
            getRowSubtitle={config.getRowSubtitle}
            onAfterMutation={() => {
              onAfterMutation?.()
            }}
            onCountDelta={(delta) => {
              if (!drawerRow) return
              invalidateNoteCount(
                {
                  exception_code: drawerRow.exception_code,
                  data_domain: drawerRow.data_domain,
                  object_id: String(drawerRow.object_id),
                  ...(variant === 'platform'
                    ? { fp_code: String(drawerRow.fp_code) }
                    : {}),
                },
                delta,
              )
            }}
          />
        )
      })()}
    </>
  )
}
